<template>
  <v-dialog
    v-model="isVisible"
    fullscreen
    scrollable
    @keydown.esc="$emit('close')"
    @click:outside="$emit('close')"
  >
    <v-card class="overlay-modal ma-0 pa-0" tile flat>
      <!-- Top bar -->
      <v-card-title v-if="viewerobj.type !== '.pdf'" dense class="text-xs py-2">
        <div class="text-xs title">
          {{ viewerobj.file ? viewerobj.file.name : '-' }}
        </div>
        <v-spacer></v-spacer>
        <v-btn v-if="showDownload" icon color="white" @click="$emit('download')">
          <v-icon>{{ mdiDownloadOutline }}</v-icon>
        </v-btn>
        <v-btn icon color="white" class="ml-2" @click="$emit('close')">
          <v-icon>{{ mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Main content -->
      <v-card-text
        class="mh-100 d-flex w-full justify-center align-center"
        :class="{ 'pa-0': viewerobj.type === '.pdf' }"
      >
        <v-progress-circular
          v-if="loading"
          :size="50"
          color="white"
          indeterminate
        ></v-progress-circular>

        <div v-else class="h-full w-full">
          <!-- PDF preview -->
          <vue-pdf-app
            v-if="viewerobj.type === '.pdf'"
            :file-name="viewerobj.file.name"
            :pdf="viewerobj.path"
            theme="dark"
          >
            <template #footer>
              <div class="text-sm font-medium">{{ viewerobj.file.name }}</div>
            </template>
            <template #toolbar-right-append>
              <v-icon class="mt-1 mr-2" color="white" @click="$emit('close')">
                {{ mdiClose }}
              </v-icon>
            </template>
          </vue-pdf-app>

          <!-- Image / SVG preview -->
          <div v-else>
            <v-btn
              v-if="showNextPrevious"
              icon
              dark
              class="icon-left"
              :disabled="prevDisabled"
              @click="$emit('previous')"
            >
              <v-icon color="white">{{ mdiArrowLeftCircleOutline }}</v-icon>
            </v-btn>

            <div class="img-cnt text-center">
              <div v-if="viewerobj.type === '.svg'">
                <div v-html="svgTag"></div>
              </div>
              <img
                v-else
                ref="imgEl"
                class="preview-img ma-auto"
                :src="getPath"
                :style="{
                  width: naturalWidth * zoom + 'px',
                  height: 'auto'
                }"
                @load="onImageLoad"
              />
            </div>

            <v-btn
              v-if="showNextPrevious"
              icon
              dark
              class="icon-right"
              :disabled="nextDisabled"
              @click="$emit('next')"
            >
              <v-icon color="white">{{ mdiArrowRightCircleOutline }}</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <!-- Bottom actions -->
      <v-card-actions v-if="viewerobj.type !== '.pdf'" class="py-3" small dense>
        <v-spacer></v-spacer>
        <v-btn-toggle dense>
          <v-btn icon small color="white" @click="zoomIn">
            <v-icon>{{ mdiMagnifyPlusOutline }}</v-icon>
          </v-btn>
          <v-btn icon small color="white" @click="zoomOut">
            <v-icon>{{ mdiMagnifyMinusOutline }}</v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiDownloadOutline,
  mdiArrowLeftCircleOutline,
  mdiArrowRightCircleOutline,
  mdiClose,
  mdiMagnifyPlusOutline,
  mdiMagnifyMinusOutline,
} from '@mdi/js'
import VuePdfApp from 'vue-pdf-app'
import 'vue-pdf-app/dist/icons/main.css'
import { computed, ref, watch } from '@vue/composition-api'
import axios from 'axios'

export default {
  components: { VuePdfApp },
  props: {
    isVisible: { type: Boolean, default: true },
    showDownload: { type: Boolean, default: true },
    viewerobj: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: true, required: true },
    prevDisabled: { type: Boolean },
    nextDisabled: { type: Boolean },
    showNextPrevious: { type: Boolean, default: true },
    isTools: { type: Boolean },
  },
  setup(props) {
    const svgTag = ref(null)
    const zoom = ref(1)
    const imgEl = ref(null)
    const naturalWidth = ref(0)

    const getPath = computed(() => props.viewerobj.path)

    const fetchSvgTag = () => {
      axios.get(props.viewerobj.path).then(data => {
        svgTag.value = data.data
      })
    }

    const onImageLoad = () => {
      if (imgEl.value) {
        naturalWidth.value = imgEl.value.naturalWidth
        zoom.value = 1 // reset zoom to natural size
      }
    }

    const zoomIn = () => {
      if (zoom.value >= 3) return
      zoom.value += 0.2
    }
    const zoomOut = () => {
      if (zoom.value > 0.4) zoom.value -= 0.2
    }

    watch(
      () => props.viewerobj.path,
      () => {
        if (props.viewerobj.type === '.svg' && props.viewerobj.path) {
          fetchSvgTag()
        }
      },
      { immediate: true }
    )

    return {
      zoom,
      getPath,
      onImageLoad,
      zoomIn,
      zoomOut,
      imgEl,
      naturalWidth,
      svgTag,
      mdiDownloadOutline,
      mdiArrowLeftCircleOutline,
      mdiArrowRightCircleOutline,
      mdiClose,
      mdiMagnifyPlusOutline,
      mdiMagnifyMinusOutline,
    }
  },
}
</script>

<style scoped>
.title {
  color: white;
  font-size: 16px !important;
}
.mh-100 {
  height: 100vh;
}
.overlay-modal {
  background: rgb(0 0 0 / 85%);
}
.img-cnt {
  min-height: calc(100vh - 108px);
  display: grid;
  place-items: center;
}
.preview-img {
  border-radius: 8px;
  max-width: none; /* prevents Vue from shrinking it */
}
.icon-left {
  position: fixed;
  top: 50%;
  left: 16px;
}
.icon-right {
  position: fixed;
  top: 50%;
  right: 16px;
}
</style>
